"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type MapsContextValue = {
  isLoaded: boolean;
  loadError: Error | null;
  /** Call before using the Maps API; safe to call multiple times (deduped). */
  ensureLoaded: () => Promise<void>;
};

const MapsContext = createContext<MapsContextValue | null>(null);

let mapsScriptPromise: Promise<void> | null = null;

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (!mapsScriptPromise) {
    mapsScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        mapsScriptPromise = null;
        reject(new Error("Failed to load Google Maps JavaScript API"));
      };
      document.head.appendChild(script);
    });
  }
  return mapsScriptPromise;
}

export function MapsProvider({ children }: { children: ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const ensureLoaded = useCallback(async () => {
    const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim();
    if (!key) {
      setLoadError(new Error("Missing NEXT_PUBLIC_GOOGLE_MAPS_API_KEY"));
      setIsLoaded(false);
      return;
    }
    try {
      await loadGoogleMapsScript(key);
      setIsLoaded(true);
      setLoadError(null);
    } catch (e: unknown) {
      const err = e instanceof Error ? e : new Error(String(e));
      setLoadError(err);
      setIsLoaded(false);
    }
  }, []);

  const value = useMemo(
    () => ({ isLoaded, loadError, ensureLoaded }),
    [isLoaded, loadError, ensureLoaded]
  );

  return (
    <MapsContext.Provider value={value}>{children}</MapsContext.Provider>
  );
}

export function useGoogleMaps(): MapsContextValue {
  const ctx = useContext(MapsContext);
  if (!ctx) {
    throw new Error("useGoogleMaps must be used within MapsProvider");
  }
  return ctx;
}
